import { apiClient } from "./client";

// Release 1.5.0 Home (O-R64 role start pages, O-R65 pinned tiles, O-R66 saved filters). Everything here is the signed-in
// person's own; the server checks every page, tile and filter against the policy that protects it.

export type StartViewKey = "plant-summary" | "planning" | "shop-dispatch" | "purchasing" | "hub" | "wall" | "planner-cockpit" | "logistics";

export interface StartPage {
  path: string;
  source: "personal" | "role";
  role: string;
  view: StartViewKey;
  viewLabel: string;
  viewDescription: string;
  mainPath: string;
  mainLabel: string;
  secondaryPath: string | null;
  secondaryLabel: string | null;
  shop: string | null;
  shopNote: string | null;
  personalPath: string | null;
  personalLabel: string | null;
  personalHidden: boolean;
  valueKinds: Record<string, string>;
}

export type TileKind = "page" | "kpi" | "filter" | "work-order";

export interface PinnedTile {
  id: number;
  kind: TileKind;
  target: string;
  label: string;
  path: string;
  position: number;
}

export interface PinnedTiles {
  tiles: PinnedTile[];
  hiddenCount: number;
  count: number;
  max: number;
  valueKinds: Record<string, string>;
}

export interface SavedFilter {
  id: number;
  screen: string;
  screenLabel: string;
  name: string;
  query: string;
  path: string;
  isDefault: boolean;
  pinned: boolean;
  updatedAtUtc: string;
}

export interface SavedFilters {
  filters: SavedFilter[];
  maxPerScreen: number;
  valueKinds: Record<string, string>;
}

export interface StartView {
  key: StartViewKey;
  label: string;
  description: string;
  mainPath: string;
  mainLabel: string;
}

export interface RoleStartPage {
  role: string;
  view: StartViewKey;
  viewLabel: string;
  defaultView: StartViewKey;
  isDefault: boolean;
  editable: boolean;
  updatedAtUtc: string | null;
  updatedBy: string | null;
}

export interface RoleStartPages {
  roles: RoleStartPage[];
  views: StartView[];
  valueKinds: Record<string, string>;
}

export const HOME_QK = {
  start: ["home", "start"] as const,
  tiles: ["home", "tiles"] as const,
  filters: (screen?: string) => ["home", "filters", screen ?? ""] as const,
  filtersRoot: ["home", "filters"] as const,
  rolePages: ["admin", "start-pages"] as const
} as const;

export const homeApi = {
  getStart: async (): Promise<StartPage> => (await apiClient.get<StartPage>("/home/start")).data,
  setStart: async (path: string, label?: string): Promise<StartPage> => (await apiClient.put<StartPage>("/home/start", { path, label })).data,
  resetStart: async (): Promise<StartPage> => (await apiClient.delete<StartPage>("/home/start")).data,

  getTiles: async (): Promise<PinnedTiles> => (await apiClient.get<PinnedTiles>("/home/tiles")).data,
  pin: async (kind: TileKind, target: string, label?: string): Promise<PinnedTiles> =>
    (await apiClient.post<PinnedTiles>("/home/tiles", { kind, target, label })).data,
  unpin: async (id: number): Promise<PinnedTiles> => (await apiClient.delete<PinnedTiles>(`/home/tiles/${id}`)).data,
  reorder: async (ids: number[]): Promise<PinnedTiles> => (await apiClient.put<PinnedTiles>("/home/tiles/order", { ids })).data,

  getFilters: async (screen?: string): Promise<SavedFilters> =>
    (await apiClient.get<SavedFilters>("/home/filters", { params: screen ? { screen } : undefined })).data,
  createFilter: async (screen: string, name: string, query: string, isDefault: boolean): Promise<SavedFilter> =>
    (await apiClient.post<SavedFilter>("/home/filters", { screen, name, query, isDefault })).data,
  updateFilter: async (id: number, patch: { name?: string; isDefault?: boolean }): Promise<SavedFilter> =>
    (await apiClient.put<SavedFilter>(`/home/filters/${id}`, patch)).data,
  deleteFilter: async (id: number): Promise<void> => {
    await apiClient.delete(`/home/filters/${id}`);
  },

  getRoleStartPages: async (): Promise<RoleStartPages> => (await apiClient.get<RoleStartPages>("/admin/start-pages")).data,
  updateRoleStartPage: async (role: string, view: StartViewKey, reason: string): Promise<RoleStartPages> =>
    (await apiClient.put<RoleStartPages>(`/admin/start-pages/${encodeURIComponent(role)}`, { view, reason })).data
};
